<script setup lang="ts">
import { helpers } from '@vuelidate/validators'

import type { CoveragePlan } from '~/config'
import type { Contract } from '~/layers/forms/models'
import { Coverage, Form } from '~/layers/forms/models'
import type {
  CoverageComparatorItemExpose,
  CoverageComparatorListExpose,
} from '~/layers/forms/types/coverageComparatorDefinedExpose'

interface Props {
  modelValue: Contract
  insurable: Insurable
  isUserChosing?: boolean
}

const props = defineProps<Props>()

const emits = defineEmits(['update:modelValue', 'selected'])
const model = useVModel(props, 'modelValue', emits)

const { RemoteConfigurationStore, LocalConfigurationStore } = stores()

const coverageTypeCode = ref<string | null>(
  LocalConfigurationStore.value?.coveragePlanConfig?.defaultGroup ??
    LocalConfigurationStore.value?.coveragePlanConfig?.plans?.[0]?.group ??
    null
)

const selectedPlan = ref<CoveragePlan | null>(
  LocalConfigurationStore.value?.coveragePlanConfig?.plans.find(
    (p) => p.code === LocalConfigurationStore.value?.coveragePlanConfig?.defaultPlan
  ) ?? null
)

const planOptions = computed(() => {
  return {
    groups: LocalConfigurationStore.value?.coveragePlanConfig?.groups ?? [],
    plans:
      LocalConfigurationStore.value?.coveragePlanConfig?.plans.filter(
        (plan) => plan.group === coverageTypeCode.value
      ) ?? [],
  }
})

const { visible: isDialogVisible, show: showDetails, hide: hideDetails } = useVisible()

const focus = useFocus()

const criteriaContainer = useTemplateRef('criteria-container')

const v$ = useValidation(
  () => ({
    argentoInsuranceProductId: {
      required: helpers.withMessage(
        'Para continuar es necesario que seleccione un plan de seguro.',
        () =>
          !!model.value.coverage.argentoInsuranceProductId &&
          !!model.value.coverage.argentoInsurancePlanId
      ),
    },
  }),
  model.value.coverage,
  {
    $scope: Form.coverage,
    $registerAs: Form.coverage,
  }
)

const associatedCompaniesInfo = computed(
  () => LocalConfigurationStore.value?.associatedCompanies ?? []
)

const {
  execute: fetchCoveragesAvaliablesCompanies,
  data: avaliablesCompanies,
  status: avaliablesCompaniesStatus,
} = useSiteApi('/api/BrokerProductos/GroupedBySubRamo', {
  method: 'GET',
  query: computed(() => ({
    BrokerId: RemoteConfigurationStore.value?.broker?.id,
    SubRamoId: RemoteConfigurationStore.value?.subRamo?.id,
  })),
  transform: (input?: SiteApiSchema<'BrokerProductoGroupBySubRamoModel'>[]) => {
    if (input)
      return input
        .find((item) => item.subRamoId === RemoteConfigurationStore.value?.subRamo?.id)
        ?.items?.sort((a, b) => {
          const getCompanyScore = (id?: number | null) =>
            associatedCompaniesInfo.value.find((c) => c.argentoInsuranceId === id)?.score ?? 0

          return getCompanyScore(b.emisorId) - getCompanyScore(a.emisorId)
        })
    else return []
  },
  immediate: true,
})

const coveragesContainerElement = templateRef('coverages-container')
const companyCoveragePlans = templateRef<CoverageComparatorItemExpose[]>('company-coverage-plans')

const quotationsStatus = computed(() => ({
  avaliable: {
    someIsPending: companyCoveragePlans.value?.some(
      (company) => company?.getQuotationStatus() === 'pending'
    ),
    allRady: companyCoveragePlans.value?.every(
      (company) => company?.getQuotationStatus() === 'success'
    ),
  },
  selectedStatus:
    companyCoveragePlans.value
      ?.find((company) => companyIsSelected(company?.getCompanyId()))
      ?.getQuotationStatus() ?? null,
}))

const fetchQuotations = useDebounceFn(
  async (
    { planCode, selectedOnly }: { planCode?: string | null; selectedOnly?: boolean } = {
      selectedOnly: false,
    }
  ) => {
    if (planCode) {
      selectedPlan.value =
        LocalConfigurationStore.value?.coveragePlanConfig?.plans.find((p) => p.code === planCode) ??
        null
    }

    let form: Form | null = null

    switch (props.insurable.branch) {
      case 'Auto':
        form = Form.vehicle
        break
      case 'Viajes':
        form = Form.travel
        break
      case 'Mascotas':
        form = Form.pet
        break
    }

    if (form) {
      await v$.value?.[form]?.$validate()
      const invalidForm = v$.value?.[form]?.$invalid
      if (invalidForm) {
        focus({ target: criteriaContainer.value })
        return
      }
    }

    companyCoveragePlans.value?.forEach((company) => {
      const companyId = company?.getCompanyId()
      if (selectedOnly && !companyIsSelected(companyId)) return
      company?.fetchPlan({
        autoSelect: companyIsSelected(companyId),
        planCode: selectedPlan.value?.code ?? undefined,
      })
    })

    if (!selectedOnly) {
      focus({ target: coveragesContainerElement.value })
      showMoreQuotesIndicatorClosed.value = false
    }
  },
  100
)

const lastCompanyCoveragePlanIndicator = ref<HTMLElement | null>(null)

const { y: lastCompanyCoveragePlanIndicatorBoundingY } = useElementBounding(
  lastCompanyCoveragePlanIndicator
)

const showMoreQuotesIndicatorClosed = ref(true)
const toast = useToast()

const showMoreQuotesIndicator = () => {
  if (showMoreQuotesIndicatorClosed.value) return

  toast.removeAllGroups()
  toast.add({
    severity: 'secondary',
    summary: 'Más polizas abajo',
    group: 'coverage-comparator',
    styleClass: 'rounded-2xl',
    life: 5000,
  })
  showMoreQuotesIndicatorClosed.value = true
}

const removeMoreQuotesIndicator = () => {
  toast.removeGroup('coverage-comparator')
}

const onSelected = ({ coverage, pasive }: { coverage: Coverage; pasive?: boolean }) => {
  model.value.coverage = coverage
  if (!pasive) emits('selected')
}

const companyIsSelected = (companyId?: number | null) =>
  companyId ? companyId === model.value.coverage.argentoInsuranceCompanyId : false

const accordionRef = ref<('benefits' | 'details')[]>([])
const updateAccordionRef = (v: ('benefits' | 'details')[]) => {
  accordionRef.value = v
}

watch(lastCompanyCoveragePlanIndicatorBoundingY, (n) => {
  if (n > 0) showMoreQuotesIndicator()
  else removeMoreQuotesIndicator()
})

watch(
  () => props.isUserChosing,
  (n, o) => {
    if (!o && Boolean(n)) model.value.coverage = Coverage.load()
  }
)

defineExpose<CoverageComparatorListExpose>({
  fetchQuotations,
  getQuotationsStatus: () => quotationsStatus.value,
})
</script>

<template>
  <div
    :id="`${Form.coverage}-form`"
    class="flex flex-col gap-8"
  >
    <FormsCoverageTypeForm
      v-if="planOptions?.groups.length"
      v-model="coverageTypeCode"
    />
    <div ref="criteria-container">
      <FormsCoverageCriteria
        v-model="model"
        :coverage-type-code
      >
        <template #action>
          <PvButton
            fluid
            style-as-input
            @click="fetchQuotations()"
          >
            <span class="font-bold uppercase">Cotizar</span>
          </PvButton>
        </template>
      </FormsCoverageCriteria>
    </div>

    <ProcessingComponent
      v-if="avaliablesCompaniesStatus === 'pending'"
      loading-message="Cargando..."
    />
    <template
      v-else-if="
        avaliablesCompaniesStatus === 'error' ||
        (avaliablesCompaniesStatus === 'success' && !avaliablesCompanies?.length)
      "
    >
      <PvMessage
        severity="warn"
        class="flex flex-col h-[8.4rem] justify-center"
      >
        <div class="flex flex-col gap-1 items-center">
          <div>
            <span class="icon-[ph--info-bold] text-2xl" />
          </div>
          <div class="text-sm"> Hubo un problema al consultar las aseguradoras </div>
          <PvButton
            severity="warn"
            class="w-full"
            @click="fetchCoveragesAvaliablesCompanies()"
          >
            <span class="icon-[ph--arrow-counter-clockwise-bold]" />
            Reintentar
          </PvButton>
        </div>
      </PvMessage>
    </template>

    <FormsGlobalContainer v-else>
      <template
        v-if="avaliablesCompaniesStatus === 'success' && avaliablesCompanies?.length"
        #default="{ sectionClasses }"
      >
        <div class="col-span-full flex items-center justify-between">
          <FormsGlobalTitle
            text="Seleccione una cobertura"
            icon-class="icon-[ph--shield-check-bold]"
          />
          <PvSplitButton
            v-if="planOptions.plans.length > 1"
            class="flex justify-between"
            fluid
            :model="
              planOptions.plans?.map((option) => ({
                label: option.name.toUpperCase(),
                command: () => fetchQuotations({ planCode: option.code }),
              }))
            "
          >
            <span class="font-bold uppercase">Cotizar</span>
            <template #dropdownicon>
              <div class="text-nowrap flex flex-nowrap gap-1 uppercase font-bold items-center">
                {{ selectedPlan?.name }}
                <span class="icon-[ph--caret-down-bold]" />
              </div>
            </template>
            <template #item="{ item }">
              <div class="w-full px-4 py-1 text-right">
                {{ item.label }}
              </div>
            </template>
          </PvSplitButton>
        </div>

        <FormsCoverageNote class="col-span-full" />
        <div
          ref="coverages-container"
          class="col-span-full"
          :class="sectionClasses({ cols: avaliablesCompanies.length > 2 ? 3 : 2 })"
        >
          <div
            v-for="(company, i) in avaliablesCompanies"
            :key="company.emisorId ?? i"
            :ref="
              (el: Element | globalThis.ComponentPublicInstance | null) => {
                if (avaliablesCompanies && i === avaliablesCompanies?.length - 1) {
                  lastCompanyCoveragePlanIndicator = el as HTMLElement
                }
              }
            "
          >
            <FormsCoverageComparatorResumeDinamic
              v-if="insurable.branch"
              ref="company-coverage-plans"
              :product-branch="insurable.branch"
              :argento-insurance-data="company"
              :insurable="insurable"
              :coverage-type-code
              :selected="companyIsSelected(company.emisorId)"
              :is-user-chosing
              :sync-accordion-value="$viewport.isGreaterOrEquals('md') ? accordionRef : undefined"
              @selected="onSelected"
              @accordion-change="updateAccordionRef"
            >
              <template
                v-if="false"
                #action
              >
                <PvButton
                  class="aspect-square place-self-end self-start p-0 top-2 right-2"
                  rounded
                  @click="showDetails()"
                >
                  <span class="icon-[ph--info-bold] text-xl text-current" />
                </PvButton>
              </template>
            </FormsCoverageComparatorResumeDinamic>
          </div>
        </div>
      </template>
    </FormsGlobalContainer>

    <PvDialog
      v-model:visible="isDialogVisible"
      :closable="false"
      modal
      class="relative max-w-[90%] md:w-[75rem]"
    >
      <div class="absolute right-2 top-2 z-10">
        <PvButton
          class="aspect-square place-self-end self-start p-0"
          rounded
          @click="hideDetails()"
        >
          <span class="icon-[ph--x-circle-bold] text-4xl text-current" />
        </PvButton>
      </div>
      <div class="p-5">
        <ImageComponent src="/img/Auto_multiple_detalle_poliza.png" />
      </div>
    </PvDialog>
  </div>

  <PvToast
    position="bottom-center"
    group="coverage-comparator"
    @close="showMoreQuotesIndicatorClosed = true"
  >
    <template #container="{ message, closeCallback }">
      <div class="flex w-full items-center justify-between gap-5 rounded-xl p-4">
        <div class="flex items-center gap-2 font-bold">
          <span class="icon-[ph--arrow-square-down-bold] text-3xl" />
          <span>{{ message.summary }}</span>
        </div>
        <PvButton
          label="Cerrar"
          size="small"
          @click="closeCallback"
        />
      </div>
    </template>
  </PvToast>
</template>
